import type { Subevent, SubeventStatus } from "@/modules/events/types/eventDetailsApi"
import type { I18nLocale } from "@/modules/i18n/types"

import { format, isAfter, isBefore } from "date-fns"

import { getCurrentLocaleDate, getFullDateFormatFromLocale } from "@/modules/dates/dateFns"

type GroupSubeventsByDateParams = {
  subevents: Subevent[]
  locale: I18nLocale
}

type GroupSubeventsOutput = {
  date: string
  subevents: Subevent[]
}[]

// Sort subevents by date
export const getSubeventsGroupedByDate = ({ subevents, locale }: GroupSubeventsByDateParams): GroupSubeventsOutput => {
  const groupedSubevents: GroupSubeventsOutput = []
  const dateLocale = getCurrentLocaleDate(locale)
  const dateFormat = getFullDateFormatFromLocale(locale)

  if (subevents.length === 0) return []

  // sort groups chronologically
  const sortedSubevents = subevents.sort((firstSubevent, nextSubevent) => {
    if (isBefore(new Date(firstSubevent.startAt), new Date(nextSubevent.startAt))) return -1
    if (isAfter(new Date(firstSubevent.startAt), new Date(nextSubevent.startAt))) return 1
    return 0
  })

  sortedSubevents.forEach(slot => {
    const formattedDate = format(new Date(slot.startAt), dateFormat, { locale: dateLocale })
    const matchingDateIndex = groupedSubevents.findIndex(item => item.date === formattedDate)

    // if it's the first slot on that day
    if (matchingDateIndex === -1) {
      groupedSubevents.push({
        date: formattedDate,
        subevents: [
          {
            ...slot,
          },
        ],
      })
    } else {
      groupedSubevents[matchingDateIndex].subevents.push(slot)
    }
  })

  return groupedSubevents
}

/**
 * Match subevents (from event data) with subeventStatuses (from registrationStatus) thanks to the ID
 * @param {Subevent[]} subevent - subevent list from event data
 * @param {SubeventStatus[]} subeventStatuses - subevent list from registrationStatus data
 * @returns {Subevent[]} - subevent list updated with dynamic data (from status)
 */

type getUpdatedSubeventsWithStatusParams = {
  subevents: Subevent[]
  subeventStatuses: SubeventStatus[]
}
export const getUpdatedSubeventsWithStatus = ({
  subevents,
  subeventStatuses,
}: getUpdatedSubeventsWithStatusParams): Subevent[] => {
  const updatedlist = subevents.map((subevent: Subevent) => {
    // Find matching ID in both lists
    const matchingSubeventStatus = subeventStatuses.find(status => status.id === subevent.id)

    // Add status infos to subevent infos
    return {
      ...subevent,
      ...matchingSubeventStatus,
    }
  })
  return updatedlist
}
